import { gql, useQuery } from "@apollo/client";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { InlineSpinner } from "components/base/Spinner";
import useDateRange from "hooks/useDateRange";
import { useModals } from "ModalProvider";
import moment from "moment";
import { useState } from "react";
import { FaChartLine } from "react-icons/fa";
import { RiArrowUpDownLine } from "react-icons/ri";
import XLSX from "xlsx";
import DateRangePicker from "../DateRangePicker";
import ProductDetailView from "./ProductDetailView";
import ProductProductionHistoryView from "./ProductProductionHistoryView";

const FETCH_PRODUCTION_TIME_IN_PLANS = gql`
  query FETCH_PRODUCTION_TIME_IN_PLANS(
    $startDate: DateTime!
    $endDate: DateTime!
  ) {
    productionTimeInPlans(startDate: $startDate, endDate: $endDate) {
      id
      name
      inPlans
      avgDays
      planLineIds
    }
  }
`;

const Products = () => {
  const [{ start, end }, { setStart, setEnd }] = useDateRange(6);
  const { loading, error, data } = useQuery(FETCH_PRODUCTION_TIME_IN_PLANS, {
    variables: { startDate: start, endDate: end },
  });
  const modal = useModals();
  const largeModal = useModals();
  const [sortBy, setSortBy] = useState("DEFAULT");

  function exportXlxs() {
    const dateRange = `${moment(start).format("YYYY-MM-DD")}_${moment(end).format("YYYY-MM-DD")}`;
    const sheet = XLSX.utils.json_to_sheet(
      data.productionTimeInPlans.map((i) => ({
        id: i.id,
        name: i.name,
        inPlans: i.inPlans,
        avgDays: i.avgDays,
      })),
    );

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, "Production Days");
    XLSX.writeFile(wb, `Production Days_${dateRange}.xlsx`);
  }

  function showDetail(product) {
    modal.present({
      title: "Plans",
      subtitle: product.name,
      children: <ProductDetailView product={product} />,
    });
  }

  function showHistory(product) {
    largeModal.present({
      title: "Production History",
      maxWidth: "max-w-7xl",
      subtitle: product.name,
      children: (
        <ProductProductionHistoryView
          product={product}
          startDate={start}
          endDate={end}
        />
      ),
    });
  }

  return (
    <div className="p-6 bg-white dark:bg-gray-800 text-sm">
      <div className="flex justify-between">
        <DateRangePicker
          start={start}
          end={end}
          setStart={setStart}
          setEnd={setEnd}
        />

        <div>
          <Button onClick={exportXlxs}>Export</Button>
        </div>
      </div>

      {loading ? (
        <div className="p-10">
          <InlineSpinner />
        </div>
      ) : error ? (
        <Errors error={error} />
      ) : (
        <div className="-mx-2">
          <table className="with-line mt-4">
            <thead>
              <tr>
                <th>Product</th>
                <th className="text-right">
                  <div className="flex items-center justify-end space-x-1">
                    <span>In Plans</span>
                    <Button
                      onClick={() =>
                        setSortBy(
                          sortBy === "IN_PLANS_DESC"
                            ? "IN_PLANS_ASC"
                            : "IN_PLANS_DESC",
                        )
                      }
                    >
                      <RiArrowUpDownLine />
                    </Button>
                  </div>
                </th>
                <th className="text-right">
                  <div className="flex items-center justify-end space-x-1">
                    <span>Ave Days</span>
                    <Button
                      onClick={() =>
                        setSortBy(
                          sortBy === "AVG_DAYS_DESC"
                            ? "AVG_DAYS_ASC"
                            : "AVG_DAYS_DESC",
                        )
                      }
                    >
                      <RiArrowUpDownLine />
                    </Button>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {[...data.productionTimeInPlans]
                .sort((a, b) => {
                  if (sortBy === "IN_PLANS_DESC") {
                    return b.inPlans - a.inPlans;
                  }
                  if (sortBy === "IN_PLANS_ASC") {
                    return a.inPlans - b.inPlans;
                  }
                  if (sortBy === "AVG_DAYS_DESC") {
                    return b.avgDays - a.avgDays;
                  }
                  if (sortBy === "AVG_DAYS_ASC") {
                    return a.avgDays - b.avgDays;
                  }
                  return 0; // Default sorting (or GraphQL ordering)
                })
                .map((product, index) => (
                  <ProductRow
                    index={index}
                    product={product}
                    key={index}
                    showDetail={showDetail}
                    showHistory={showHistory}
                  />
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const ProductRow = ({ product, showDetail, showHistory }) => {
  return (
    <tr>
      <td>
        <div className="flex items-center space-x-3">
          <div>{product.name}</div>
          <Button onClick={() => showDetail(product)}>view plans</Button>
          <Button onClick={() => showHistory(product)}>
            <FaChartLine />
          </Button>
        </div>
      </td>
      <td className="text-right">{product.inPlans}</td>
      <td className="text-right">{product.avgDays}</td>
    </tr>
  );
};

export default Products;
